import React, { useEffect, useMemo, useRef, useState } from 'react';
import F1ApiPortalViewModel from './F1ApiPortal.viewmodel';
import { FaEdit, FaEye, FaPlus, FaSave, FaTrash } from 'react-icons/fa';
import { dbDateTimeToDisplayString } from 'commons/common';
import { AuthenticationResponse } from 'data/schemas/auth';
import ModalApiSpecDetail from './components/ModalApiSpecDetail';
import { VscDiscard, VscListFilter } from 'react-icons/vsc';
import { Select } from '@chakra-ui/react';

function OwnerDropdown({
    value,
    onChange,
    employees,
    disabled
}: {
    value: string;
    onChange: (citizenId: string) => void;
    employees: AuthenticationResponse[];
    disabled: boolean;
}) {
    const [search, setSearch] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const displayValue = employees.find(e => e.ctizenID === value)?.nickName ?? value;
    const filtered = employees.filter(e =>
        (e.nickName ?? '').toLowerCase().includes(search.toLowerCase())
    );

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setIsOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div
            className="relative"
            ref={ref}
        >
            <input
                value={isOpen ? search : displayValue}
                disabled={disabled}
                onClick={() => {
                    if (!disabled) {
                        setIsOpen(true);
                        setSearch('');
                    }
                }}
                onChange={e => setSearch(e.target.value)}
                className="w-full border-none bg-transparent p-1 text-sm focus:bg-gray-50"
            />
            {isOpen && (
                <div className="absolute z-50 mt-1 max-h-48 min-w-[160px] overflow-auto rounded border border-gray-200 bg-white shadow-lg">
                    {filtered.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-400">No results</div>
                    ) : (
                        filtered.map((e, i) => (
                            <div
                                key={i}
                                className="cursor-pointer px-3 py-1 text-sm hover:bg-blue-50"
                                onMouseDown={() => {
                                    onChange(e.ctizenID ?? '');
                                    setIsOpen(false);
                                    setSearch('');
                                }}
                            >
                                {e.nickName}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

const API_TABLE_COLUMNS = [
    '',
    'API Group',
    'Module',
    'API Name',
    'Namespace',
    'Class Name',
    'Function Name',
    'Status',
    'Owner',
    'Updated By',
    'Updated On',
    'Remark'
];

const REQUIRED_FIELDS = ['API Group', 'Module'];

function F1ApiPortalView() {
    const {
        specs,
        employee,
        selectedSpec,
        specFiles,
        detailModal,
        addApiSpec,
        saveApiSpec,
        handleAction,
        handleInputChange,
        openDetail,
        onUploadApiSpecFiles
    } = F1ApiPortalViewModel();

    const [activeFilters, setActiveFilters] = useState<('api_group' | 'module')[]>([]);
    const [columnFilters, setColumnFilters] = useState<{ api_group: string[]; module: string[] }>({
        api_group: [],
        module: []
    });
    const filterRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
                setActiveFilters([]);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filterOptions = useMemo(() => {
        const uniqueApiGroups = Array.from(
            new Set(specs.filter(s => s.api_group).map(s => s.api_group))
        ).sort((a, b) => a.localeCompare(b));
        const uniqueModules = Array.from(
            new Set(specs.filter(s => s.module).map(s => s.module))
        ).sort((a, b) => a.localeCompare(b));

        return {
            api_group: uniqueApiGroups,
            module: uniqueModules
        };
    }, [specs]);

    const filteredSpecs = useMemo(
        () =>
            specs
                .map((spec, index) => ({ spec, index }))
                .filter(({ spec }) => {
                    const matchesApiGroup =
                        !activeFilters.includes('api_group') ||
                        !columnFilters.api_group.length ||
                        columnFilters.api_group.includes(spec.api_group ?? '');
                    const matchesModule =
                        !activeFilters.includes('module') ||
                        !columnFilters.module.length ||
                        columnFilters.module.includes(spec.module ?? '');
                    return matchesApiGroup && matchesModule;
                }),
        [specs, columnFilters]
    );

    return (
        <div className="p-2">
            <div className="mb-2 flex items-center justify-between">
                <h4 className="text-lg font-bold text-gray-800">API Specs</h4>
            </div>
            <div className="mb-2 flex justify-between">
                {/*<div className="flex items-end gap-2">
                    <input className="w-full rounded-sm border-2 bg-transparent p-1 text-sm focus:bg-gray-50" />
                    <button className="flex items-center rounded bg-gray-50 px-2 py-1 text-sm text-gray-900 transition-colors hover:bg-gray-100">
                        Search
                    </button>
                </div>*/}
                <div className="flex gap-2">
                    <button
                        onClick={saveApiSpec}
                        className="flex items-center rounded bg-green-50 px-2 py-1 text-sm text-green-600 transition-colors hover:bg-green-100"
                    >
                        <FaSave className="mr-1" /> Save
                    </button>
                    <button
                        onClick={addApiSpec}
                        className="flex items-center rounded bg-blue-50 px-2 py-1 text-sm text-blue-600 transition-colors hover:bg-blue-100"
                    >
                        <FaPlus className="mr-1" />
                        Add
                    </button>
                </div>
            </div>
            {specs.length > 0 ? (
                <div className="max-h-screen overflow-auto rounded border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                {API_TABLE_COLUMNS.map((col, i) => (
                                    <th
                                        key={i}
                                        className="p-2 text-left text-sm font-bold uppercase text-gray-500"
                                    >
                                        <span className="flex items-center gap-1">
                                            {col}
                                            {REQUIRED_FIELDS.includes(col) && (
                                                <div
                                                    className="relative"
                                                    ref={
                                                        activeFilters.includes(
                                                            col === 'API Group'
                                                                ? 'api_group'
                                                                : 'module'
                                                        )
                                                            ? filterRef
                                                            : undefined
                                                    }
                                                >
                                                    <button
                                                        type="button"
                                                        className={`rounded p-1 hover:bg-gray-200 ${
                                                            activeFilters.includes(
                                                                col === 'API Group'
                                                                    ? 'api_group'
                                                                    : 'module'
                                                            )
                                                                ? 'text-blue-600'
                                                                : 'text-gray-500'
                                                        }`}
                                                        onClick={() =>
                                                            setActiveFilters(prev => {
                                                                const filter =
                                                                    col === 'API Group'
                                                                        ? 'api_group'
                                                                        : 'module';
                                                                return prev.includes(filter)
                                                                    ? prev.filter(f => f !== filter)
                                                                    : [...prev, filter];
                                                            })
                                                        }
                                                    >
                                                        <VscListFilter size={16} />
                                                    </button>
                                                    {activeFilters.includes(
                                                        col === 'API Group' ? 'api_group' : 'module'
                                                    ) && (
                                                        <div className="absolute right-0 top-full z-50 mt-1 min-w-[170px] rounded border border-gray-200 bg-white p-2 normal-case shadow-lg">
                                                            <Select
                                                                size="sm"
                                                                value={
                                                                    col === 'API Group'
                                                                        ? columnFilters.api_group
                                                                        : columnFilters.module
                                                                }
                                                                onChange={e =>
                                                                    setColumnFilters(prev => ({
                                                                        ...prev,
                                                                        [col === 'API Group'
                                                                            ? 'api_group'
                                                                            : 'module']:
                                                                            e.target.value
                                                                    }))
                                                                }
                                                            >
                                                                <option value="">All</option>
                                                                {(col === 'API Group'
                                                                    ? filterOptions.api_group
                                                                    : filterOptions.module
                                                                ).map(option => (
                                                                    <option
                                                                        key={option}
                                                                        value={option}
                                                                    >
                                                                        {option}
                                                                    </option>
                                                                ))}
                                                            </Select>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {filteredSpecs.map(({ spec, index }) => (
                                <tr
                                    key={`idx${index}-${spec.id}`}
                                    className={`${spec.changeable ? 'bg-blue-200' : 'hover:bg-gray-200'}`}
                                >
                                    {spec.removeable ? (
                                        <td className="px-3 py-1 text-center">
                                            <button
                                                onClick={() => handleAction('remove', index)}
                                                className="p-1 text-red-500 hover:text-red-700"
                                            >
                                                <FaTrash size={12} />
                                            </button>
                                        </td>
                                    ) : spec.changeable ? (
                                        <td className="px-3 py-1 text-center">
                                            <button
                                                onClick={() => handleAction('discard', index)}
                                                className="p-1 text-purple-500 hover:text-purple-700"
                                            >
                                                <VscDiscard size={14} />
                                            </button>
                                        </td>
                                    ) : (
                                        <td className="px-3 py-1 text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                <button
                                                    onClick={() => handleAction('edit', index)}
                                                    className="p-1 text-yellow-500 hover:text-yellow-700"
                                                >
                                                    <FaEdit size={14} />
                                                </button>
                                                <button
                                                    onClick={() => openDetail(index)}
                                                    className="p-1 text-blue-400 hover:text-blue-600"
                                                >
                                                    <FaEye size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    )}
                                    <td className="px-3 py-1">
                                        <input
                                            value={spec.api_group}
                                            disabled={!spec.changeable}
                                            onChange={e =>
                                                handleInputChange(
                                                    index,
                                                    'api_group',
                                                    e.target.value
                                                )
                                            }
                                            className="w-full border-none bg-transparent p-1 text-sm focus:bg-gray-50"
                                        />
                                    </td>
                                    <td className="px-3 py-1">
                                        <input
                                            value={spec.module}
                                            disabled={!spec.changeable}
                                            onChange={e =>
                                                handleInputChange(index, 'module', e.target.value)
                                            }
                                            className="w-full border-none bg-transparent p-1 text-sm focus:bg-gray-50"
                                        />
                                    </td>
                                    <td className="px-3 py-1">
                                        <input
                                            value={spec.api_name}
                                            disabled={!spec.changeable}
                                            onChange={e =>
                                                handleInputChange(index, 'api_name', e.target.value)
                                            }
                                            className="w-full border-none bg-transparent p-1 text-sm focus:bg-gray-50"
                                        />
                                    </td>
                                    <td className="px-3 py-1">
                                        <input
                                            value={spec.api_namespace}
                                            disabled={!spec.changeable}
                                            onChange={e =>
                                                handleInputChange(
                                                    index,
                                                    'api_namespace',
                                                    e.target.value
                                                )
                                            }
                                            className="w-full border-none bg-transparent p-1 text-sm focus:bg-gray-50"
                                        />
                                    </td>
                                    <td className="px-3 py-1">
                                        <input
                                            value={spec.classname}
                                            disabled={!spec.changeable}
                                            onChange={e =>
                                                handleInputChange(
                                                    index,
                                                    'classname',
                                                    e.target.value
                                                )
                                            }
                                            className="w-full border-none bg-transparent p-1 text-sm focus:bg-gray-50"
                                        />
                                    </td>
                                    <td className="px-3 py-1">
                                        <input
                                            value={spec.function_name}
                                            disabled={!spec.changeable}
                                            onChange={e =>
                                                handleInputChange(
                                                    index,
                                                    'function_name',
                                                    e.target.value
                                                )
                                            }
                                            className="w-full border-none bg-transparent p-1 text-sm focus:bg-gray-50"
                                        />
                                    </td>
                                    <td className="px-3 py-1">
                                        <div className="flex items-center space-x-2">
                                            <button
                                                type="button"
                                                role="switch"
                                                aria-checked={spec.status === 'active'}
                                                disabled={!spec.changeable}
                                                onClick={() => {
                                                    const newStatus =
                                                        spec.status === 'active'
                                                            ? 'inactive'
                                                            : 'active';
                                                    handleInputChange(index, 'status', newStatus);
                                                }}
                                                className={`
                                                    relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent
                                                    transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                                                    ${spec.status === 'active' ? 'bg-green-500' : 'bg-gray-300'}
                                                    ${!spec.changeable ? 'cursor-not-allowed opacity-50' : 'hover:opacity-80'}
                                                `}
                                            >
                                                <span
                                                    aria-hidden="true"
                                                    className={`
                                                        pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0
                                                        transition duration-200 ease-in-out
                                                        ${spec.status === 'active' ? 'translate-x-5' : 'translate-x-0'}
                                                    `}
                                                />
                                            </button>
                                        </div>
                                    </td>
                                    <td className="px-3 py-1">
                                        <OwnerDropdown
                                            value={spec.owner ?? ''}
                                            onChange={citizenId =>
                                                handleInputChange(index, 'owner', citizenId)
                                            }
                                            employees={employee}
                                            disabled={!spec.changeable}
                                        />
                                    </td>
                                    <td className="px-3 py-1">
                                        <input
                                            value={spec.updated_by}
                                            disabled
                                            className="w-full border-none bg-transparent p-1 text-sm focus:bg-gray-50"
                                        />
                                    </td>
                                    <td className="px-3 py-1">
                                        <input
                                            value={dbDateTimeToDisplayString(spec.updated_on ?? '')}
                                            disabled
                                            className="w-full border-none bg-transparent p-1 text-sm focus:bg-gray-50"
                                        />
                                    </td>
                                    <td className="px-3 py-1">
                                        <input
                                            value={spec.remark}
                                            disabled={!spec.changeable}
                                            onChange={e =>
                                                handleInputChange(index, 'remark', e.target.value)
                                            }
                                            className="w-full border-none bg-transparent p-1 text-sm focus:bg-gray-50"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className="text-sm italic text-gray-500">No API Spec</p>
            )}

            <ModalApiSpecDetail
                isOpen={detailModal.isOpen}
                onClose={detailModal.onClose}
                spec={selectedSpec}
                employees={employee}
                specFiles={specFiles}
                onUploadFiles={onUploadApiSpecFiles}
            />
        </div>
    );
}

export default F1ApiPortalView;
